import type {
  AssignmentTripLateness,
  TripLatenessLeg,
} from '@bambi/api-client/schemas';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ItineraryStopsByTripLocationType = Partial<
  Record<'pickup' | 'dropoff', TripLatenessLeg>
>;

export type LatenessSeverity = 'low' | 'medium' | 'high';

export interface ItineraryState {
  stopsByTripId: Record<
    string,
    Record<string, ItineraryStopsByTripLocationType | undefined> | undefined
  >;
  lateTripIdMap: Record<string, Record<string, boolean> | undefined>;
  // Cached organization settings that affect trip lateness
  organizationSettings: {
    ambulatory_load_seconds?: number;
    ambulatory_unload_seconds?: number;
    wheelchair_load_seconds?: number;
    wheelchair_unload_seconds?: number;
    wheelchair_xl_load_seconds?: number;
    wheelchair_xl_unload_seconds?: number;
    broda_chair_load_seconds?: number;
    broda_chair_unload_seconds?: number;
    geri_chair_load_seconds?: number;
    geri_chair_unload_seconds?: number;
    stretcher_load_seconds?: number;
    stretcher_unload_seconds?: number;
    lateness_alert_low_seconds?: number | null;
    lateness_alert_medium_seconds?: number | null;
    lateness_alert_high_seconds?: number | null;
  };
}

export const initialState: ItineraryState = {
  stopsByTripId: {},
  lateTripIdMap: {},
  organizationSettings: {},
};

// `tripLateness.trips` contains every trip on the itinerary, not just late
// ones, so lateness has to be derived from the individual legs.
export function getLateTripIds(
  tripLateness: AssignmentTripLateness | null | undefined
) {
  return Object.entries<ItineraryStopsByTripLocationType>(
    tripLateness?.trips ?? {}
  )
    .filter(([, legs]) =>
      Boolean(legs.pickup?.is_late || legs.dropoff?.is_late)
    )
    .map(([tripId]) => tripId);
}

function getLegLatenessSeverity(
  leg: TripLatenessLeg | undefined,
  organizationSettings: ItineraryState['organizationSettings']
): LatenessSeverity | null {
  if (!leg?.is_late) {
    return null;
  }
  if (leg.late_by_seconds == null) {
    return 'medium';
  }

  const {
    lateness_alert_low_seconds: lowSeconds = null,
    lateness_alert_medium_seconds: mediumSeconds = 0,
    lateness_alert_high_seconds: highSeconds = null,
  } = organizationSettings;

  if (highSeconds !== null && leg.late_by_seconds >= highSeconds) {
    return 'high';
  }
  if (mediumSeconds !== null && leg.late_by_seconds >= mediumSeconds) {
    return 'medium';
  }
  if (lowSeconds !== null && leg.late_by_seconds >= lowSeconds) {
    return 'low';
  }
  return null;
}

export function getHighestLatenessSeverity(
  severities: (LatenessSeverity | null)[]
): LatenessSeverity | null {
  return (
    (['high', 'medium', 'low'] as const).find((severity) =>
      severities.includes(severity)
    ) ?? null
  );
}

export function getTripLatenessSeverity(
  legs: ItineraryStopsByTripLocationType | undefined,
  organizationSettings: ItineraryState['organizationSettings']
) {
  return getHighestLatenessSeverity([
    getLegLatenessSeverity(legs?.pickup, organizationSettings),
    getLegLatenessSeverity(legs?.dropoff, organizationSettings),
  ]);
}

export const itinerarySlice = createSlice({
  name: 'itinerary',
  initialState,
  reducers: {
    reset(state) {
      state.stopsByTripId = initialState.stopsByTripId;
      state.lateTripIdMap = initialState.lateTripIdMap;
    },
    cacheTripItineraryLateness(
      state,
      action: PayloadAction<{
        tripLateness: AssignmentTripLateness | null | undefined;
        assignmentId: string;
      }>
    ) {
      const { tripLateness, assignmentId } = action.payload;
      if (!tripLateness) {
        state.stopsByTripId[assignmentId] = {};
        state.lateTripIdMap[assignmentId] = {};
        return;
      }

      const lateTrips = getLateTripIds(tripLateness);

      state.stopsByTripId[assignmentId] = tripLateness.trips;
      state.lateTripIdMap = {
        ...state.lateTripIdMap,
        [assignmentId]: lateTrips.reduce<Record<string, boolean>>(
          (acc, id) => ({
            [id]: true,
            ...acc,
          }),
          {}
        ),
      };
    },
    cacheOrganizationSettings(
      state,
      action: PayloadAction<ItineraryState['organizationSettings']>
    ) {
      state.organizationSettings = action.payload;
    },
  },
  selectors: {
    selectStopsByTripId(state, assignmentId: string, tripId: string) {
      return state.stopsByTripId[assignmentId]?.[tripId];
    },
    selectIsTripLate(state, assignmentId: string, tripId: string) {
      const trips = state.lateTripIdMap[assignmentId];
      return Boolean(trips?.[tripId]);
    },
    selectOrganizationSettings(state) {
      return state.organizationSettings;
    },
    selectLegLatenessSeverity(
      state,
      assignmentId: string,
      tripId: string,
      purpose: 'pickup' | 'dropoff'
    ) {
      return getLegLatenessSeverity(
        state.stopsByTripId[assignmentId]?.[tripId]?.[purpose],
        state.organizationSettings
      );
    },
    selectTripLatenessSeverity(state, assignmentId: string, tripId: string) {
      return getTripLatenessSeverity(
        state.stopsByTripId[assignmentId]?.[tripId],
        state.organizationSettings
      );
    },
  },
});

export const { cacheTripItineraryLateness, reset, cacheOrganizationSettings } =
  itinerarySlice.actions;
export const {
  selectStopsByTripId,
  selectIsTripLate,
  selectOrganizationSettings,
  selectLegLatenessSeverity,
  selectTripLatenessSeverity,
} = itinerarySlice.selectors;
